import { useEffect } from "react";
import { Terminal as TerminalIcon, Settings, Globe, Minus, X } from "lucide-react";

// Mini "Windows desktop" popup window opened when a PC is clicked in the
// topology. Three application icons in a grid: Command Prompt, IP
// Configuration, Web Browser. Dark title bar with minimize / close
// controls (minimize just dismisses the window — same as close).
export default function PcMenu({
  open,
  deviceLabel,
  onClose,
  onPickConsole,
  onPickIpConfig,
  onPickBrowser,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const title = `${deviceLabel || "PC"} - Desktop`;

  const launch = (handler) => () => {
    handler?.();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="pc-menu-backdrop"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#1B1B22] border border-zinc-700 shadow-2xl rounded-md overflow-hidden font-mono"
        onClick={(e) => e.stopPropagation()}
        data-testid="pc-desktop-window"
      >
        {/* Title bar */}
        <div className="flex items-center justify-between bg-[#0B0B10] border-b border-zinc-800 px-3 py-2">
          <div className="flex items-center gap-2 text-xs text-zinc-200 font-semibold tracking-wide">
            <span className="w-2 h-2 rounded-full bg-[#00E5FF]" />
            {title}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onClose}
              className="w-6 h-6 flex items-center justify-center text-zinc-400 hover:bg-zinc-700 hover:text-white rounded-sm"
              title="Minimize"
              data-testid="pc-desktop-minimize"
            >
              <Minus size={12} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-6 h-6 flex items-center justify-center text-zinc-400 hover:bg-red-600 hover:text-white rounded-sm"
              title="Close"
              data-testid="pc-desktop-close"
            >
              <X size={12} />
            </button>
          </div>
        </div>

        {/* Desktop "wallpaper" with app icon grid */}
        <div className="bg-gradient-to-br from-[#0F1420] via-[#10172A] to-[#1B1B22] p-6">
          <div className="grid grid-cols-3 gap-4">
            <DesktopIcon
              label="Command Prompt"
              testid="pc-app-console"
              onClick={launch(onPickConsole)}
              icon={<TerminalIcon size={28} />}
              accent="#00E5FF"
            />
            <DesktopIcon
              label="IP Configuration"
              testid="pc-app-ipconfig"
              onClick={launch(onPickIpConfig)}
              icon={<Settings size={28} />}
              accent="#00E5FF"
            />
            <DesktopIcon
              label="Web Browser"
              testid="pc-app-browser"
              onClick={launch(onPickBrowser)}
              icon={<Globe size={28} />}
              accent="#00E5FF"
            />
          </div>
        </div>

        {/* Taskbar */}
        <div className="flex items-center justify-between bg-[#0B0B10] border-t border-zinc-800 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          <span>{deviceLabel || "pc"}</span>
          <span>online</span>
        </div>
      </div>
    </div>
  );
}

function DesktopIcon({ label, icon, onClick, testid, accent }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex flex-col items-center gap-2 group focus:outline-none"
    >
      <span
        className="w-16 h-16 flex items-center justify-center rounded-md bg-[#1A2034] border border-zinc-700 group-hover:border-[#00E5FF] group-hover:bg-[#11253A] transition-colors"
        style={{ color: accent }}
      >
        {icon}
      </span>
      <span className="text-[11px] font-sans text-zinc-200 group-hover:text-white text-center leading-tight max-w-[88px]">
        {label}
      </span>
    </button>
  );
}
